import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import { HttpErrorInterceptorService } from '@app/shared/errors/http-error-interceptor.service';
import { RUNTIME_CONFIG } from '@environments/runtime-config.token';
import {
  BehaviorSubject,
  catchError,
  EMPTY,
  merge,
  Observable,
  Subject,
  Subscription,
  switchMap,
  tap,
  timer,
  map,
} from 'rxjs';
import {
  Anomaly,
  ANOMALY_AUTHORS,
  ANOMALY_REFRESH_MS,
  AnomalyDisplayState,
  AnomalyRequest,
  VisibleAnomaly,
} from './anomaly.model';
import { AnomalyApiError, anomalyErrorMessage } from './anomaly-presentation';
import { canonicalRoles, formerRoleNames } from '@app/features/users/role-names';

export interface IAnomalyService {
  /** Les anomalies à afficher en bandeau à l'utilisateur connecté. */
  visible$: Observable<VisibleAnomaly[]>;
  /** Toutes les anomalies, archivées comprises : la direction seule les reçoit. */
  all$: Observable<Anomaly[]>;
  start(): void;
  refresh(): void;
  create(request: AnomalyRequest): Observable<Anomaly>;
  update(id: string, request: AnomalyRequest): Observable<Anomaly>;
  archive(id: string): Observable<Anomaly>;
  setDisplayState(id: string, displayState: AnomalyDisplayState): Observable<void>;
}

/** Statuts d'une relecture manquée, qu'on tait : la suivante reprendra dans la minute. */
const SILENT_REFRESH_STATUSES = [0, 500, 502, 503, 504];

/** Refus d'une écriture que l'application dit elle-même en mots (voir `anomalyErrorMessage`). */
const WORDED_WRITE_STATUSES = [400, 404, 409];

@Injectable({ providedIn: 'root' })
export class AnomalyService implements IAnomalyService {
  private readonly http = inject(HttpClient);
  private readonly url = `${inject(RUNTIME_CONFIG).apiUrl}/anomalies`;
  private readonly authenticatedUserService = inject(AUTHENTICATED_USER_SERVICE);
  private readonly snackBar = inject(MatSnackBar);
  private readonly _visibleSubject = new BehaviorSubject<VisibleAnomaly[]>([]);
  private readonly _allSubject = new BehaviorSubject<Anomaly[]>([]);
  private readonly refresh$ = new Subject<void>();
  private polling?: Subscription;

  constructor() {
    const httpErrorInterceptorService = inject(HttpErrorInterceptorService);
    for (const status of SILENT_REFRESH_STATUSES) {
      httpErrorInterceptorService.addIgnoreHttpStatusForUrlBeginningBy(status, 'GET', this.url);
    }
    for (const status of WORDED_WRITE_STATUSES) {
      for (const method of ['POST', 'PUT']) {
        httpErrorInterceptorService.addIgnoreHttpStatusForUrlBeginningBy(status, method, this.url);
      }
    }
    inject(DestroyRef).onDestroy(() => this.polling?.unsubscribe());
  }

  get visible$(): Observable<VisibleAnomaly[]> {
    return this._visibleSubject.asObservable();
  }

  get all$(): Observable<Anomaly[]> {
    return this._allSubject.asObservable();
  }

  /** Lance la relecture des anomalies, tout de suite puis chaque minute (décision 2026-027/08). */
  start(): void {
    if (this.polling) return;
    this.polling = merge(timer(0, ANOMALY_REFRESH_MS), this.refresh$)
      .pipe(switchMap(() => this.load()))
      .subscribe();
  }

  /** Relit sans attendre la minute ; démarre la relecture si elle ne tournait pas encore. */
  refresh(): void {
    if (!this.polling) {
      this.start();
      return;
    }
    this.refresh$.next();
  }

  /** Une relecture qui échoue laisse les bandeaux déjà affichés. */
  private load(): Observable<unknown> {
    const visible = this.http
      .get<VisibleAnomaly[]>(`${this.url}/visible`)
      .pipe(tap((anomalies) => this._visibleSubject.next(anomalies)));
    const all = this.authenticatedUserService.hasAnyRole(ANOMALY_AUTHORS)
      ? this.http
          .get<Anomaly[]>(this.url)
          .pipe(tap((anomalies) => this._allSubject.next(anomalies.map(readAnomaly))))
      : EMPTY;
    return merge(visible.pipe(catchError(() => EMPTY)), all.pipe(catchError(() => EMPTY)));
  }

  create(request: AnomalyRequest): Observable<Anomaly> {
    return this.http.post<Anomaly>(this.url, writeAnomaly(request)).pipe(
      map(readAnomaly),
      tap(() => this.refresh()),
    );
  }

  update(id: string, request: AnomalyRequest): Observable<Anomaly> {
    return this.http.put<Anomaly>(`${this.url}/${id}`, writeAnomaly(request)).pipe(
      map(readAnomaly),
      tap(() => this.refresh()),
    );
  }

  archive(id: string): Observable<Anomaly> {
    return this.http.post<Anomaly>(`${this.url}/${id}/archive`, null).pipe(
      map(readAnomaly),
      tap(() => this.refresh()),
    );
  }

  /**
   * Réduire, fermer ou rouvrir un bandeau : l'écran suit aussitôt, l'API le mémorise. Si l'anomalie
   * a changé entre-temps (archivée, plus ciblée, de nouveau en cours), on le dit et on relit.
   */
  setDisplayState(id: string, displayState: AnomalyDisplayState): Observable<void> {
    this._visibleSubject.next(
      this._visibleSubject.value.map((anomaly) =>
        anomaly.id === id ? { ...anomaly, displayState } : anomaly,
      ),
    );
    return this.http.put<void>(`${this.url}/${id}/display-state`, { displayState }).pipe(
      tap({
        error: (error: AnomalyApiError) => {
          const message = anomalyErrorMessage(error, 'display');
          if (message) this.snackBar.open(message, 'OK');
          this.refresh();
        },
      }),
      catchError(() => EMPTY),
    );
  }
}

/** Les rôles visés, lus sous leur nouveau nom (cadrage 2026-031, décision 02). */
function readAnomaly(anomaly: Anomaly): Anomaly {
  return { ...anomaly, targetRoles: canonicalRoles(anomaly.targetRoles) };
}

/** Les rôles visés, écrits sous leur ancien nom, que l'API accepte avant comme après #338. */
function writeAnomaly(request: AnomalyRequest): AnomalyRequest {
  return { ...request, targetRoles: formerRoleNames(request.targetRoles) };
}
